import { useEffect, useMemo, useState } from "react";
import AOS from "aos";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import ContactFloat from "@/components/ContactFloat";

type PartnerCategory = "all" | "support" | "afterSales" | "guarantee" | "private" | "telecom" | "payment";

type Partner = {
  id: string;
  name: string;
  category: PartnerCategory;
  description: string;
  link: string;
};

const categories: PartnerCategory[] = ["all", "support", "afterSales", "guarantee", "private", "telecom", "payment"];

export default function Tool() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<PartnerCategory>("all");

  useEffect(() => {
    AOS.init({
      offset: 100,
      easing: "ease-out-back",
      duration: 1000,
      delay: 100,
    });
  }, []);

  const partners = useMemo<Partner[]>(
    () => t("tool.partners", { returnObjects: true }) as Partner[],
    [t]
  );

  const filteredPartners = useMemo(() => {
    if (activeCategory === "all") return partners;
    return partners.filter((p) => p.category === activeCategory);
  }, [partners, activeCategory]);

  return (
    <>
      <Helmet>
        <title>{t("tool.title")}</title>
        <meta name="keywords" content={t("tool.keywords")} />
        <meta name="description" content={t("tool.description")} />
      </Helmet>
      <ContactFloat />

      {/* Banner */}
      <div className="bg-[#10549a]">
        <section
          className="w-full bg-[url('/images/tool_01.jpg')] bg-no-repeat bg-center"
          data-aos="fade-right"
          data-aos-offset="100"
          data-aos-duration="500"
        >
          <div className="w-full min-h-[320px] md:h-[400px] md:min-h-0 bg-black/40 relative">
            <div className="w-1200 mx-auto px-4 sm:px-6">
              <div className="text-center text-white pt-[120px] pb-16 md:pb-0 md:pt-[150px] relative">
                <h2
                  className="text-2xl sm:text-[28px] md:text-[30px] font-bold"
                  data-aos="fade-right"
                  data-aos-offset="100"
                  data-aos-duration="500"
                >
                  {t("tool.banner.title")}
                </h2>
                <span
                  className="block h-[4px] w-[50px] bg-white mx-auto mt-3"
                  data-aos-delay="300"
                  data-aos="zoom-in"
                ></span>
                <p
                  className="mt-5 text-base md:text-[18px] px-1"
                  data-aos="fade-left"
                  data-aos-offset="100"
                  data-aos-duration="500"
                >
                  {t("tool.banner.subtitle")}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Partners Section */}
      <section className="relative w-full bg-white py-[60px] lg:py-[80px]">
        <div className="w-1200 mx-auto px-4 sm:px-6">
          {/* Section Header */}
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="font-bold text-[#222] text-[28px] lg:text-[34px] pb-4">
              {t("tool.intro.title")}
            </h2>
            <p className="text-[#666] text-[16px] lg:text-[18px] max-w-[800px] mx-auto">
              {t("tool.intro.subtitle")}
            </p>
          </div>

          {/* Category Filter */}
          <div
            className="flex flex-wrap justify-center gap-3 mb-12"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`
                  px-5 py-2 rounded-full text-[14px] font-medium transition-all duration-300
                  ${
                    activeCategory === category
                      ? "bg-[#0f5f95] text-white shadow-md"
                      : "bg-white text-[#666] border border-[#ddd] hover:border-[#0f5f95] hover:text-[#0f5f95]"
                  }
                `}
              >
                {t(`tool.categories.${category}`)}
              </button>
            ))}
          </div>

          {/* Partners Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredPartners.map((partner, index) => (
              <a
                key={partner.id}
                href={partner.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white rounded-xl border border-[#e8e8e8] overflow-hidden 
                           hover:shadow-lg hover:border-[#0f5f95] transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index % 4 * 100}
              >
                {/* Partner Card */}
                <div className="p-6 h-full flex flex-col">
                  {/* Logo Area */}
                  <div className="h-[80px] flex items-center justify-center mb-4 bg-[#f8f9fa] rounded-lg group-hover:bg-[#ecf4fa] transition-colors">
                    <span className="text-[#0f5f95] font-bold text-xl tracking-wide">
                      {partner.name}
                    </span>
                  </div>

                  {/* Category Tag */}
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-[#ecf4fa] text-[#0f5f95] text-[12px] rounded-full">
                      {t(`tool.categories.${partner.category}`)}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-[#666] text-[14px] leading-[1.6] flex-grow line-clamp-4">
                    {partner.description}
                  </p>

                  {/* Learn More Link */}
                  <div className="mt-4 pt-4 border-t border-[#f0f0f0]">
                    <span className="text-[#0f5f95] text-[14px] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Learn more
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Empty State */}
          {filteredPartners.length === 0 && (
            <div className="text-center py-12 text-[#666]" data-aos="fade-up">
              {t("No partners found in this category")}
            </div>
          )}

          {/* Intro Description */}
          <div
            className="mt-16 text-center max-w-[900px] mx-auto"
            data-aos="fade-up"
          >
            <p className="text-[#666] text-[16px] leading-[1.8]">
              {t("tool.intro.description")}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
